// Кнопка «поделиться». Живёт в панели сравнения: делятся именно итогом — тем,
// какой вариант дешевле и быстрее, — а не библиотекой блоков.
// Гостю она видна, но ведёт ко входу: ссылку надо где-то хранить, а у гостя
// на сервере ничего нет.
import { useEffect, useState, type ReactElement } from 'react';
import { Modal } from '../ui/shell';
import { useI18n, useT } from '../i18n';
import { useAuth } from '../auth';
import { useStore } from '../store';
import { useTrips } from '../trips';
import { fetchMyShare, publishShare, revokeShare, shareUrl, type Share } from './api';
import { tripPreview } from './preview';
import { useShareView } from './useShareView';
import styles from './ShareButton.module.css';

function ShareIcon(): ReactElement {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="18" cy="5.5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="18.5" r="2.5" />
      <path d="M8.2 10.8 15.8 6.7M8.2 13.2l7.6 4.1" />
    </svg>
  );
}

function ShareDialog({ onClose }: { onClose(): void }): ReactElement {
  const { lang, t } = useI18n();
  const view = useShareView();
  const state = useStore();
  const { currentId } = useTrips();
  const [share, setShare] = useState<Share | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchMyShare(currentId).then((res) => {
      if (cancelled) return;
      if (res.ok) setShare(res.value.share);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currentId]);

  async function handlePublish(): Promise<void> {
    setBusy(true);
    setFailed(false);
    // Превью снимается ровно с того состояния, копию которого получает ссылка,
    // и на том языке, на котором человек сейчас работает.
    const res = await publishShare(currentId, { lang, summary: tripPreview(state, t) });
    setBusy(false);
    if (res.ok) setShare(res.value.share);
    else setFailed(true);
  }

  async function handleRevoke(): Promise<void> {
    setBusy(true);
    await revokeShare(currentId);
    setBusy(false);
    setShare(null);
    setCopied(false);
  }

  async function handleCopy(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Буфер обмена недоступен (нет https или отказано в праве) — тогда
      // пользователь копирует руками, поле для этого уже выделено.
      setCopied(false);
    }
  }

  // Вид едет в самом адресе, а не в записи на сервере: тему и кегль человек
  // может поменять после публикации, и переспрашивать сервер ради этого незачем
  // — ссылку всё равно копируют прямо сейчас и прямо отсюда.
  const url = share ? shareUrl(share.id, view) : '';

  return (
    <Modal title={t('share.title')} onClose={onClose}>
      <div className={styles.body}>
        <p className={styles.hint}>{t('share.hint')}</p>

        {loading && <p className={styles.hint}>{t('auth.busy')}</p>}
        {failed && <p className={styles.error}>{t('share.failed')}</p>}

        {!loading && !share && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={handlePublish}>
            {t('share.create')}
          </button>
        )}

        {share && (
          <>
            <div className={styles.linkRow}>
              <input
                className="input mono"
                type="text"
                readOnly
                value={url}
                onFocus={(e) => e.currentTarget.select()}
                aria-label={t('share.link')}
              />
              <button type="button" className="btn" onClick={() => void handleCopy(url)}>
                {t(copied ? 'share.copied' : 'share.copy')}
              </button>
            </div>

            {/* Вид зашит в адрес — это видно по хвосту ссылки, но не понятно
                без объяснения; иначе параметры выглядят мусором, который
                хочется отрезать перед отправкой. */}
            <p className={styles.meta}>{t('share.look')}</p>

            <p className={styles.meta}>
              {t('share.updated', { date: new Date(share.updatedAt).toLocaleString() })}
              {' · '}
              {t('share.views', { count: share.views })}
            </p>

            <div className={styles.actions}>
              {/* Ссылка — застывшая копия: пока не нажмут «обновить», читатель
                  видит ровно то, чем поделились. */}
              <button type="button" className="btn" disabled={busy} onClick={handlePublish}>
                {t('share.update')}
              </button>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={handleRevoke}>
                {t('share.revoke')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export interface ShareButtonProps {
  /**
   * 'icon' — квадратная кнопка для полосы инструментов;
   * 'wide' — крупная кнопка с подписью во всю ширину панели сравнения.
   */
  variant?: 'icon' | 'wide';
}

export function ShareButton({ variant = 'icon' }: ShareButtonProps = {}): ReactElement | null {
  const t = useT();
  const { isGuest, status, requestSignIn } = useAuth();
  const [open, setOpen] = useState(false);

  if (status === 'loading') return null;

  const wide = variant === 'wide';

  return (
    <>
      <span
        className={wide ? styles.wideWrap : undefined}
        title={isGuest ? t('auth.guest.locked') : t('share.tip')}
      >
        <button
          type="button"
          className={wide ? `btn ${styles.wideBtn}` : `btn btn-sm ${styles.iconBtn}`}
          aria-label={wide ? undefined : t('share.title')}
          onClick={() => (isGuest ? requestSignIn() : setOpen(true))}
        >
          <ShareIcon />
          {wide && <span>{t('share.title')}</span>}
        </button>
      </span>
      {open && !isGuest && <ShareDialog onClose={() => setOpen(false)} />}
    </>
  );
}
